<template>
  <div class="tree-edit-container">
    <!-- Zona para soltar en la raíz SIEMPRE visible cuando hay drag -->
    <div
      v-if="draggingNode && isRoot"
      class="drop-root-zone"
      @dragover.prevent="onDragOverRoot"
      @drop="onDropRoot"
      :class="{ 'drop-active': dropType === 'root' }"
    >
      <div class="drop-indicator">
        <i class="fas fa-plus-circle"></i>
        <span>Soltar aquí para mover a la raíz</span>
      </div>
    </div>

    <div class="tree-items">
      <div
        v-for="(node, idx) in nodes"
        v-if="node && typeof node === 'object'"
        :key="node._id || node.label || idx"
        class="tree-item-wrapper"
        @dragover="onDragOver(node, idx, $event)"
        @dragleave="onDragLeave"
        @drop="onDrop(node, idx, $event)"
      >
        <!-- Franja para insertar como hermano (antes del nodo) -->
        <div
          v-if="showSiblingDrop(idx)"
          class="drop-sibling-zone"
          :class="{ 'drop-active': dropType === 'sibling' && dropIdx === idx }"
        >
          <div class="drop-line"></div>
          <span class="drop-text">Soltar como hermano</span>
        </div>
        <div
          class="tree-item"
          :class="{
            'highlight-child': highlightChild(node),
            dragging: draggingNode === node,
            'drop-zone-child': dropType === 'child' && dropNode === node,
            'drop-zone-sibling-top': dropType === 'sibling' && dropIdx === idx,
            'drop-zone-sibling-bottom':
              dropType === 'sibling' && dropIdx === idx + 1,
          }"
          :style="{ paddingLeft: (node._indent || 0) + 20 + 'px' }"
        >
          <div class="tree-item-content">
            <!-- ICONO DE ARRASTRE -->
            <div
              class="drag-handle"
              draggable="true"
              @dragstart="onDragStart(node, $event)"
              @dragend="resetDrop"
              :title="$t('treeEdit.dragToMove')"
            >
              <i class="fas fa-grip-vertical"></i>
            </div>

            <!-- ICONO DE EXPANDIR/COLAPSAR -->
            <div
              v-if="node.children && node.children.length > 0"
              class="expand-handle"
              @click="toggleNode(node)"
            >
              <i
                :class="
                  node.expanded !== false
                    ? 'fas fa-chevron-down'
                    : 'fas fa-chevron-right'
                "
              ></i>
            </div>
            <div v-else class="expand-placeholder"></div>

            <!-- COLOR DEL ELEMENTO (se elige en el modal de editar) -->
            <span
              v-if="nodeColor(node)"
              class="item-color-dot"
              :style="{ backgroundColor: nodeColor(node) }"
              :title="$t('treeEdit.itemColor')"
            ></span>

            <!-- INPUT DEL NOMBRE -->
            <input
              v-model="node.label"
              class="tree-input"
              :disabled="!canEdit"
              @input="onLabelEdit"
              :placeholder="$t('treeEdit.elementName')"
            />

            <!-- INPUT DEL CÓDIGO CONTABLE (solo si showAccountingCode) -->
            <div v-if="showAccountingCode" class="accounting-code-wrapper">
              <input
                v-model="node.accounting_code"
                class="accounting-code-input"
                :disabled="!canEdit"
                @input="onLabelEdit"
                placeholder="Cód. Contable"
                :title="$t('treeEdit.accountingCode') || 'Código contable para asientos'"
              />
            </div>

            <!-- INPUT DEL COEFICIENTE DE CONVERSIÓN (solo si showConversionRate) -->
            <div v-if="showConversionRate" class="conversion-rate-wrapper">
              <input
                v-model.number="node.conversionRate"
                type="number"
                step="0.0001"
                class="conversion-rate-input"
                :disabled="!canEdit"
                @input="onLabelEdit"
                placeholder="Coef."
                :title="'Coeficiente de conversión a la moneda del sistema'"
              />
            </div>

            <!-- INDICADOR DE USO: uno por cada modelo que usa este valor -->
            <div v-if="hasUsageCounts" class="usage-indicator-wrapper">
              <template v-if="getUsageSources(node).length">
                <a
                  v-for="source in getUsageSources(node)"
                  :key="source.modelName + '|' + (source.groupKey || '') + '|' + source.fieldKey"
                  class="usage-indicator used clickable"
                  :href="buildUsageLink(node, source)"
                  :title="getSourceTooltip(source)"
                  @click.prevent="goToUsage(node, source)"
                >
                  <i class="fas fa-link"></i>
                  <span class="usage-count">{{ source.count }}</span>
                  <span class="usage-model">{{ source.modelLabel || source.modelName }}</span>
                  <span class="usage-where">{{ sourceWhere(source) }}</span>
                </a>
              </template>
              <span 
                v-else-if="isNodeUsed(node)"
                class="usage-indicator used"
                :title="getUsageTooltip(node)"
              >
                <i class="fas fa-link"></i>
                <span class="usage-count">{{ getUsageCount(node) }}</span>
              </span>
              <span 
                v-else
                class="usage-indicator unused"
                title="Este elemento no está siendo usado en ningún contenido"
              >
                <i class="fas fa-unlink"></i>
              </span>
            </div>

            <!-- BOTÓN DE EDITAR -->
            <button
              v-if="canEdit"
              class="edit-btn"
              @click="openEditModal(node)"
              :title="$t('treeEdit.editElement') || 'Editar elemento'"
            >
              <i class="fas fa-edit"></i>
            </button>

            <!-- BOTÓN DE ELIMINAR -->
            <button
              v-if="canEdit"
              class="delete-btn"
              @click="removeThisNode(idx)"
              :title="$t('treeEdit.deleteElement')"
            >
              <i class="fas fa-trash"></i>
            </button>
          </div>

          <!-- Vista simple: Nombre | es: … | en: … -->
          <div
            v-if="listItemHasAnyTranslation(node)"
            class="labels-preview-line"
            :title="formatListItemLabelsLine(node)"
          >
            {{ formatListItemLabelsLine(node) }}
          </div>

          <!-- HIJOS (solo si está expandido) -->
          <div
            v-if="
              node.children && node.children.length && node.expanded === true
            "
            class="children-container"
          >
            <TreeEdit
              :nodes="node.children"
              :canEdit="canEdit"
              :listId="listId"
              :isRoot="false"
              :usageCounts="usageCounts"
              :usageSources="usageSources"
              :showAccountingCode="showAccountingCode"
              :showConversionRate="showConversionRate"
              @update="
                (newNodes, operationType) =>
                  $emit('update', nodes, operationType)
              "
              @save-requested="$emit('save-requested')"
            />
          </div>

          <!-- INDICADOR DE HIJOS COLAPSADOS -->
          <div
            v-if="
              node.children &&
              node.children.length > 0 &&
              node.expanded !== true
            "
            class="collapsed-indicator"
            @click.stop="toggleNode(node)"
          >
            <i class="fas fa-chevron-right"></i>
            <span
              >{{ node.children.length }} elemento{{
                node.children.length !== 1 ? "s" : ""
              }}
              colapsado{{ node.children.length !== 1 ? "s" : "" }}</span
            >
          </div>
        </div>

        <!-- Franja para insertar como hermano (después del nodo) -->
        <div
          v-if="showSiblingDrop(idx + 1)"
          class="drop-sibling-zone"
          :class="{
            'drop-active': dropType === 'sibling' && dropIdx === idx + 1,
          }"
        >
          <div class="drop-line"></div>
          <span class="drop-text">Soltar como hermano</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import permissionsMixin from "@/mixins/permissionsMixin";
import { wencesApi } from "@/api/wencesApi";
import {
  LIST_I18N_LOCALE_CODES,
  formatListItemLabelsLine,
  listItemHasAnyTranslation,
} from "@/utils/listItemI18n";
import {
  bindListItemColorPicker,
  buildListItemColorPickerHtml,
  normalizeHexColor,
} from "@/utils/listItemColor";

function escapeHtml(s) {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/"/g, "&quot;");
}

export default {
  name: "TreeEdit",
  props: {
    nodes: {
      type: Array,
      default: () => []
    },
    listId: {
      type: String,
      default: ''
    },
    usageCounts: {
      type: Object,
      default: () => ({})
    },
    // valueId -> [{ modelName, modelLabel, fieldKey, fieldLabel, groupKey, groupLabel, count }]
    usageSources: {
      type: Object,
      default: () => ({})
    },
    showAccountingCode: {
      type: Boolean,
      default: false
    },
    showConversionRate: {
      type: Boolean,
      default: false
    }
  },
  mixins: [permissionsMixin],
  data() {
    return {
      dropType: null, // 'child', 'sibling', 'root'
      dropIdx: null,
      dropNode: null,
      draggingNode: null,
      dragStartX: 0,
    };
  },
  computed: {
    isRoot() {
      // Es raíz si no tiene padre
      return !this.$parent || !Array.isArray(this.$parent.nodes);
    },
    canEdit() {
      return (
        this.hasPermission("lists.view") ||
        this.hasPermission("lists.manage") ||
        this.hasPermission("lists.delete")
      );
    },
    hasUsageCounts() {
      return Object.keys(this.usageCounts).length > 0;
    },
  },
  methods: {
    formatListItemLabelsLine(node) {
      return formatListItemLabelsLine(node);
    },
    listItemHasAnyTranslation(node) {
      return listItemHasAnyTranslation(node);
    },
    /** Color asignado al ítem, o '' si no tiene (se muestra como punto en la fila). */
    nodeColor(node) {
      return normalizeHexColor(node && node.color);
    },
    toggleNode(node) {
      // Si no tiene estado definido, inicialmente está colapsado (false)
      if (node.expanded === undefined) {
        this.$set(node, "expanded", true); // Expandir por primera vez
      } else {
        this.$set(node, "expanded", !node.expanded); // Toggle
      }
    },
    onDragStart(node, event) {
      if (!node || typeof node !== "object") {
        console.warn("Nodo inválido en onDragStart:", node);
        return;
      }

      this.draggingNode = node;
      this.dragStartX = event.clientX;
      this.dropType = null;
      this.dropIdx = null;
      this.dropNode = null;
      // Para que el navegador muestre el "drag"
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData(
        "text/plain",
        node._id || node.label || "unknown"
      );
    },
    onDragOver(node, idx, event) {
      event.preventDefault();

      const rect = event.target.getBoundingClientRect();
      const y = event.clientY - rect.top;
      const x = event.clientX - rect.left;
      const width = rect.width;
      const height = rect.height;

      // Zona de drop como hijo (parte derecha del elemento)
      if (x > width * 0.7) {
        this.dropType = "child";
        this.dropNode = node;
        this.dropIdx = null;
      }
      // Zona de drop como hermano (parte superior)
      else if (y < height * 0.3) {
        this.dropType = "sibling";
        this.dropIdx = idx;
        this.dropNode = null;
      }
      // Zona de drop como hermano (parte inferior)
      else if (y > height * 0.7) {
        this.dropType = "sibling";
        this.dropIdx = idx + 1;
        this.dropNode = null;
      }
      // Zona central - no hacer nada
      else {
        this.dropType = null;
        this.dropIdx = null;
        this.dropNode = null;
      }
    },
    onDragOverRoot(event) {
      event.preventDefault();
      this.dropType = "root";
    },
    onDragLeave() {
      this.dropType = null;
      this.dropIdx = null;
      this.dropNode = null;
    },
    onDrop(node, idx, event) {
      if (!this.draggingNode || this.draggingNode === node) return;
      this.removeNode(this.nodes, this.draggingNode);

      if (this.dropType === "child" && this.dropNode === node) {
        if (!node.children) this.$set(node, "children", []);
        node.children.push(this.draggingNode);
      } else if (this.dropType === "root") {
        let root = this;
        while (root.$parent && Array.isArray(root.$parent.nodes)) {
          root = root.$parent;
        }
        root.nodes.push(this.draggingNode);
      } else if (this.dropType === "sibling" && this.dropIdx !== null) {
        this.nodes.splice(this.dropIdx, 0, this.draggingNode);
      }
      this.$emit("update", this.nodes, "drag-drop");
      this.resetDrop();
    },
    onDropRoot() {
      if (!this.draggingNode) return;
      // Busca el array raíz (el más alto)
      let root = this;
      while (root.$parent && Array.isArray(root.$parent.nodes)) {
        root = root.$parent;
      }
      // Elimina el nodo de su posición original
      this.removeNode(this.nodes, this.draggingNode);
      // Añade el nodo a la raíz
      root.nodes.push(this.draggingNode);
      this.$emit("update", this.nodes, "drag-drop");
      this.resetDrop();
    },
    removeNode(nodes, nodeToRemove) {
      for (let i = 0; i < nodes.length; i++) {
        if (nodes[i] === nodeToRemove) {
          nodes.splice(i, 1);
          return true;
        }
        if (
          nodes[i].children &&
          this.removeNode(nodes[i].children, nodeToRemove)
        ) {
          return true;
        }
      }
      return false;
    },
    async removeThisNode(idx) {
      const node = this.nodes[idx];
      const result = await Swal.fire({
        title: "¿Estás seguro?",
        html: `
          <div class="text-start">
            <p>Esta acción eliminará permanentemente el elemento "${node.label}".</p>
            <p class="text-danger"><strong>Advertencia:</strong> Todos los contenidos que estén utilizando este elemento se verán afectados y podrían quedar corruptos.</p>
          </div>
        `,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
        allowOutsideClick: false,
        allowEscapeKey: false,
        focusConfirm: false,
      });

      if (result.isConfirmed) {
        // Si el elemento tiene un _id válido del backend, eliminarlo del backend
        if (
          node._id &&
          node._id.length === 24 &&
          /^[0-9a-fA-F]{24}$/.test(node._id)
        ) {
          try {
            // Llamar al endpoint para eliminar el elemento específico
            await wencesApi.delete(
              `/api/gotrading/lists/${this.listId}/values/${node._id}`
            );
          } catch (error) {
            Swal.fire({
              title: "Error",
              text: "Hubo un error al eliminar el elemento del servidor.",
              icon: "error",
            });
            return; // No eliminar del frontend si falló en el backend
          }
        } else {
        }

        // Eliminar del frontend
        this.nodes.splice(idx, 1);
        this.$emit("update", this.nodes, "delete");
        // Emitir evento especial para indicar que fue eliminación del backend (no marcar cambios pendientes)
        this.$emit("backend-delete");
      }
    },
    onLabelEdit() {
      this.$emit("update", this.nodes, "label-edit");
    },
    getUsageCount(node) {
      if (!node || !node._id) return 0;
      const nodeId = node._id.toString ? node._id.toString() : node._id;
      return this.usageCounts[nodeId] || 0;
    },
    isNodeUsed(node) {
      // Check if the node itself is used
      if (this.getUsageCount(node) > 0) {
        return true;
      }
      
      // Check if any descendant is used (recursively)
      if (node.children && node.children.length > 0) {
        return node.children.some(child => this.isNodeUsed(child));
      }
      
      return false;
    },
    getUsageSources(node) {
      if (!node || !node._id) return [];
      const nodeId = node._id.toString ? node._id.toString() : node._id;
      return this.usageSources[nodeId] || [];
    },
    // Un mismo modelo puede usar el listado en varios lugares (el campo de cabecera y cada
    // grilla de renglones): sin esto, los indicadores se ven todos iguales
    sourceWhere(source) {
      return source.groupKey
        ? source.groupLabel || source.groupKey
        : source.fieldLabel || source.fieldKey;
    },
    // Clave de filtro del listado: los subcampos de un grupo se filtran como "grupo.campo"
    usageFilterKey(source) {
      return source.groupKey ? `${source.groupKey}.${source.fieldKey}` : source.fieldKey;
    },
    buildUsageLink(node, source) {
      const nodeId = node._id.toString ? node._id.toString() : node._id;
      // El nombre va tal cual está definido: hay modelos en minúscula (payment) y otros
      // con mayúscula (Contact), y forzar el caso rompía la ruta de estos últimos
      const modelName = String(source.modelName || '');
      const query = encodeURIComponent(this.usageFilterKey(source)) + '=' + encodeURIComponent(nodeId);
      return `/dynamic-model/${modelName}?${query}`;
    },
    goToUsage(node, source) {
      const nodeId = node._id.toString ? node._id.toString() : node._id;
      const modelName = String(source.modelName || '');
      this.$router
        .push({
          path: `/dynamic-model/${modelName}`,
          query: { [this.usageFilterKey(source)]: nodeId }
        })
        .catch(() => {});
    },
    getSourceTooltip(source) {
      const model = source.modelLabel || source.modelName;
      const field = source.groupKey
        ? `${source.groupLabel || source.groupKey} › ${source.fieldLabel || source.fieldKey}`
        : source.fieldLabel || source.fieldKey;
      return `${source.count} ${model} usan este valor en ${field} — click para ver el listado filtrado`;
    },
    getUsageTooltip(node) {
      const directCount = this.getUsageCount(node);
      if (directCount > 0) {
        return `Este elemento está siendo usado en ${directCount} contenido(s)`;
      }
      
      // Check if it's marked as used because of descendants
      if (node.children && node.children.length > 0) {
        const hasUsedDescendants = node.children.some(child => this.isNodeUsed(child));
        if (hasUsedDescendants) {
          return 'Este elemento tiene hijos que están siendo usados';
        }
      }
      
      return 'Este elemento está siendo usado';
    },
    showSiblingDrop(idx) {
      return this.dropType === "sibling" && this.dropIdx === idx;
    },
    highlightChild(node) {
      return this.dropType === "child" && this.dropNode === node;
    },
    resetDrop() {
      this.draggingNode = null;
      this.dragStartX = 0;
      this.dropType = null;
      this.dropIdx = null;
      this.dropNode = null;
    },
    async openEditModal(node) {
      if (!node || typeof node !== "object") return;

      // Obtener todos los nodos del árbol para el selector de padre
      const getAllNodes = (nodes) => {
        const allNodes = [];
        const collect = (ns) => {
          if (!Array.isArray(ns)) return;
          ns.forEach(n => {
            if (n && typeof n === 'object' && n.label) {
              allNodes.push(n);
              if (n.children && Array.isArray(n.children) && n.children.length > 0) {
                collect(n.children);
              }
            }
          });
        };
        collect(nodes);
        return allNodes;
      };

      // Obtener el array raíz
      let root = this;
      while (root.$parent && Array.isArray(root.$parent.nodes)) {
        root = root.$parent;
      }
      const rootNodes = root.nodes || this.nodes;
      const allNodes = getAllNodes(rootNodes);

      // Filtrar nodos que pueden ser padres (excluir el nodo actual y sus descendientes)
      const isDescendant = (n, ancestor) => {
        if (!n || !ancestor || n._id === ancestor._id) return false;
        let current = n;
        const visited = new Set();
        while (current && current.parent && !visited.has(current._id)) {
          visited.add(current._id);
          const parentId = typeof current.parent === 'string' ? current.parent : current.parent.toString();
          if (parentId === ancestor._id || parentId === ancestor.label) {
            return true;
          }
          // Buscar el nodo padre
          const parentNode = allNodes.find(p => 
            (p._id && p._id.toString() === parentId) || p.label === parentId
          );
          if (!parentNode) break;
          current = parentNode;
        }
        return false;
      };

      const parentOptions = allNodes.filter(n => {
        if (n._id === node._id) return false;
        return !isDescendant(n, node);
      });

      // Crear opciones HTML para el select
      const parentOptionsHtml = parentOptions.map(p => {
        const label = p.label || '';
        const value = p._id || p.label || '';
        const selected = (node.parent && (
          (p._id && node.parent.toString() === p._id.toString()) ||
          (p.label === node.parent)
        )) ? 'selected' : '';
        return `<option value="${value}" ${selected}>${label}</option>`;
      }).join('');

      const localeRows = LIST_I18N_LOCALE_CODES.map((code) => {
        const langName =
          this.$getLanguageName(code) || code.toUpperCase();
        const raw = node.labels && node.labels[code] != null ? node.labels[code] : "";
        return `
            <label class="form-label mb-1 small text-muted">${escapeHtml(langName)} (${escapeHtml(code)})</label>
            <input id="swal-label-${code}" class="swal2-input mb-2" value="${escapeHtml(raw)}" placeholder="${escapeHtml(code)}">`;
      }).join("");

      const { value: formValues } = await Swal.fire({
        title: this.$tn('treeEdit.editElement') || 'Editar elemento',
        html: `
          <div class="text-start">
            <label class="form-label mb-2">${this.$tn('treeEdit.elementName') || 'Nombre'}</label>
            <input id="swal-label" class="swal2-input" value="${escapeHtml(node.label || '')}" placeholder="${this.$tn('treeEdit.elementName') || 'Nombre del elemento'}">
            <label class="form-label mb-2 mt-3">${this.$tn('Parent term (optional)') || 'Padre (opcional)'}</label>
            <select id="swal-parent" class="swal2-select" style="width: 100%; padding: 0.625em;">
              <option value="">${this.$tn('No parent term (root item)') || 'Sin padre (elemento raíz)'}</option>
              ${parentOptionsHtml}
            </select>
            <hr class="my-3" />
            <label class="form-label mb-2">${this.$tn("treeEdit.itemColor")}</label>
            <p class="small text-secondary mb-2">${this.$tn("treeEdit.itemColorHint")}</p>
            ${buildListItemColorPickerHtml({
              selected: node.color,
              noneLabel: this.$tn("treeEdit.noColor"),
            })}
            <hr class="my-3" />
            <p class="small text-muted mb-2">${this.$tn("treeEdit.listTranslationsSection")}</p>
            <p class="small text-secondary mb-2">${this.$tn("treeEdit.listTranslationsHint")}</p>
            ${localeRows}
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: this.$tn('Save') || 'Guardar',
        cancelButtonText: this.$tn('Cancel') || 'Cancelar',
        focusConfirm: false,
        didOpen: () => {
          const labelInput = document.getElementById('swal-label');
          if (labelInput) labelInput.focus();
          bindListItemColorPicker(Swal.getPopup());
        },
        preConfirm: () => {
          const label = document.getElementById('swal-label')?.value?.trim();
          const parent = document.getElementById('swal-parent')?.value || null;
          const color = normalizeHexColor(
            document.getElementById('swal-item-color')?.value
          );

          if (!label) {
            Swal.showValidationMessage(this.$tn('treeEdit.elementName') || 'El nombre es requerido');
            return false;
          }

          const labels = { ...(node.labels && typeof node.labels === "object" ? node.labels : {}) };
          LIST_I18N_LOCALE_CODES.forEach((code) => {
            const el = document.getElementById(`swal-label-${code}`);
            const t = el && el.value != null ? String(el.value).trim() : "";
            if (t) labels[code] = t;
            else delete labels[code];
          });
          
          return { label, parent, labels, color };
        }
      });

      if (formValues) {
        const oldParent = node.parent;
        const newParent = formValues.parent || null;

        // Actualizar el label
        this.$set(node, 'label', formValues.label);
        // Sin color elegido se guarda `null`: el badge del valor queda neutro.
        this.$set(node, 'color', formValues.color || null);
        this.$set(
          node,
          'labels',
          formValues.labels && typeof formValues.labels === 'object'
            ? { ...formValues.labels }
            : {}
        );

        // Si cambió el parent, mover el nodo
        if (oldParent !== newParent) {
          this.$set(node, 'parent', newParent);
          
          // Remover de su posición actual
          this.removeNodeFromTree(rootNodes, node);

          // Agregar a la nueva posición
          if (newParent) {
            const parentNode = allNodes.find(p => 
              (p._id && newParent.toString() === p._id.toString()) ||
              (p.label === newParent)
            );
            if (parentNode) {
              if (!parentNode.children) {
                this.$set(parentNode, 'children', []);
              }
              parentNode.children.push(node);
              this.$set(parentNode, 'expanded', true);
            } else {
              rootNodes.push(node);
            }
          } else {
            rootNodes.push(node);
          }
        }

        // Emitir actualización y pedir guardado inmediato (evita segundo clic en "Guardar" de la página)
        this.$emit("update", this.nodes, "label-edit");
        this.$emit("save-requested");
      }
    },
    removeNodeFromTree(nodes, nodeToRemove) {
      // Buscar y remover el nodo de cualquier nivel del árbol
      for (let i = 0; i < nodes.length; i++) {
        if (nodes[i] === nodeToRemove) {
          nodes.splice(i, 1);
          return true;
        }
        if (
          nodes[i].children &&
          Array.isArray(nodes[i].children) &&
          this.removeNodeFromTree(nodes[i].children, nodeToRemove)
        ) {
          return true;
        }
      }
      return false;
    },
  },
};
</script>

<style scoped>
.tree-edit-container {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.drop-root-zone {
  margin-bottom: 12px;
  padding: 16px;
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
  text-align: center;
  transition: all 0.3s ease;
  background: #fafafa;
}

.drop-root-zone.drop-active {
  border-color: #4caf50;
  background: #e8f5e8;
  transform: scale(1.02);
}

.drop-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #666;
  font-weight: 500;
}

.drop-root-zone.drop-active .drop-indicator {
  color: #4caf50;
}

.tree-items {
  position: relative;
}

.tree-item-wrapper {
  position: relative;
  margin-bottom: 8px;
}

.drop-sibling-zone {
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 4px 0;
  opacity: 0;
  transition: all 0.2s ease;
  position: relative;
}

.drop-sibling-zone.drop-active {
  opacity: 1;
  height: 32px;
  margin: 6px 0;
}

.drop-line {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 2px;
  background: #4caf50;
  border-radius: 1px;
  transform: translateY(-50%);
}

.drop-text {
  background: #4caf50;
  color: white;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 0.85rem;
  font-weight: 500;
  z-index: 1;
  position: relative;
}

.tree-item {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin: 2px 0;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.tree-item:hover {
  border-color: #c0c0c0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.tree-item.highlight-child {
  border-color: #2196f3;
  background: #e3f2fd;
  box-shadow: 0 2px 12px rgba(33, 150, 243, 0.2);
  position: relative;
}

.tree-item.highlight-child::after {
  content: "Soltar como hijo";
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  background: #2196f3;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  z-index: 10;
}

.tree-item.dragging {
  opacity: 0.5;
  transform: rotate(2deg);
}

.drop-zone-child {
  border-color: #4caf50 !important;
  background: #e8f5e8 !important;
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.3) !important;
}

.drop-zone-child::before {
  content: "↓ Soltar como hijo";
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  background: #4caf50;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  z-index: 10;
  animation: pulse 1s infinite;
}

.drop-zone-sibling-top {
  border-top: 3px solid #ff9800 !important;
  box-shadow: 0 -2px 8px rgba(255, 152, 0, 0.3) !important;
}

.drop-zone-sibling-bottom {
  border-bottom: 3px solid #ff9800 !important;
  box-shadow: 0 2px 8px rgba(255, 152, 0, 0.3) !important;
}

@keyframes pulse {
  0% {
    transform: translateY(-50%) scale(1);
  }
  50% {
    transform: translateY(-50%) scale(1.05);
  }
  100% {
    transform: translateY(-50%) scale(1);
  }
}

.tree-item-content {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  gap: 12px;
  min-height: 48px;
}

.labels-preview-line {
  padding: 0 16px 10px 52px;
  margin-top: -4px;
  padding-top: 4px;
  font-size: 0.8rem;
  color: #6c757d;
  line-height: 1.4;
  word-break: break-word;
  border-top: 1px solid #f3f3f3;
}

.drag-handle {
  cursor: grab;
  color: #9e9e9e;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.drag-handle:hover {
  color: #666;
  background: #f5f5f5;
}

.drag-handle:active {
  cursor: grabbing;
}

.expand-handle {
  cursor: pointer;
  color: #666;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.expand-handle:hover {
  color: #333;
  background: #f0f0f0;
}

.expand-placeholder {
  width: 24px;
  height: 24px;
}

.tree-input {
  flex: 1;
  border: none;
  background: transparent;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  transition: all 0.2s ease;
}

.tree-input:focus {
  outline: none;
  background: #f8f9fa;
  box-shadow: 0 0 0 2px #e3f2fd;
}

.tree-input:disabled {
  color: #999;
  cursor: not-allowed;
}

/* Punto con el color asignado al ítem (se edita en el modal «Editar elemento»). */
.item-color-dot {
  flex-shrink: 0;
  width: 12px;
  height: 12px;
  margin-left: 4px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.25);
}

.accounting-code-wrapper {
  display: flex;
  align-items: center;
}

.accounting-code-input {
  width: 100px;
  border: 1px solid #e0e0e0;
  background: #f8f9fa;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #2196f3;
  text-align: center;
  transition: all 0.2s ease;
  font-family: monospace;
}

.accounting-code-input:focus {
  outline: none;
  background: white;
  border-color: #2196f3;
  box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2);
}

.accounting-code-input:disabled {
  color: #999;
  cursor: not-allowed;
}

.accounting-code-input::placeholder {
  color: #bdbdbd;
  font-weight: 400;
}

.conversion-rate-wrapper {
  display: flex;
  align-items: center;
}

.conversion-rate-input {
  width: 80px;
  border: 1px solid #e0e0e0;
  background: #f8f9fa;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #4caf50;
  text-align: center;
  transition: all 0.2s ease;
  font-family: monospace;
}

.conversion-rate-input:focus {
  outline: none;
  background: white;
  border-color: #4caf50;
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
}

.conversion-rate-input:disabled {
  color: #999;
  cursor: not-allowed;
}

.conversion-rate-input::placeholder {
  color: #bdbdbd;
  font-weight: 400;
}

/* Hide spinner for number input */
.conversion-rate-input::-webkit-outer-spin-button,
.conversion-rate-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.conversion-rate-input[type=number] {
  -moz-appearance: textfield;
}

.usage-indicator-wrapper {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  margin-right: 8px;
}

.usage-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  transition: all 0.2s ease;
}

.usage-indicator.used {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
}

.usage-indicator.clickable {
  cursor: pointer;
  text-decoration: none;
}

.usage-indicator.clickable:hover {
  filter: brightness(1.08);
  transform: translateY(-1px);
  color: white;
}

.usage-model {
  font-size: 0.7rem;
  font-weight: 500;
  opacity: 0.9;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.usage-where {
  font-size: 0.65rem;
  font-weight: 400;
  opacity: 0.85;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-left: 4px;
  border-left: 1px solid rgba(255, 255, 255, 0.45);
}

.usage-indicator.unused {
  background: linear-gradient(135deg, #ffc107 0%, #fd7e14 100%);
  color: #212529;
}

.usage-indicator i {
  font-size: 0.7rem;
}

.usage-count {
  font-size: 0.75rem;
}

.edit-btn {
  background: none;
  border: none;
  color: #2196f3;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.edit-btn:hover {
  background: #e3f2fd;
  color: var(--bs-primary);
  transform: scale(1.1);
}

.delete-btn {
  background: none;
  border: none;
  color: #f44336;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.delete-btn:hover {
  background: #ffebee;
  color: #d32f2f;
  transform: scale(1.1);
}

.children-container {
  margin-left: 20px;
  border-left: 2px solid #e0e0e0;
  padding-left: 16px;
  margin-top: 12px;
  position: relative;
}

.children-container::before {
  content: "";
  position: absolute;
  left: -1px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: linear-gradient(to bottom, #e0e0e0 0%, transparent 100%);
}

/* Animaciones */
.tree-item {
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .tree-item-content {
    padding: 8px 12px;
    gap: 8px;
  }

  .tree-input {
    font-size: 16px; /* Evita zoom en iOS */
  }

  .children-container {
    margin-left: 12px;
    padding-left: 12px;
  }
}

/* Estados de carga */
.tree-item.loading {
  opacity: 0.6;
  pointer-events: none;
}

.tree-item.loading::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.4),
    transparent
  );
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

/* Indicador de elementos colapsados */
.collapsed-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  margin: 4px 0;
  margin-left: 40px; /* Alineado con el contenido del item */
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 1px solid #dee2e6;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 13px;
  color: #6c757d;
}

.collapsed-indicator:hover {
  background: linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%);
  border-color: #adb5bd;
  color: #495057;
  transform: translateX(2px);
}

.collapsed-indicator i {
  font-size: 12px;
  transition: transform 0.2s ease;
}

.collapsed-indicator:hover i {
  transform: translateX(2px);
}

/* Animación para expansión/colapso */
.children-container {
  transition: all 0.3s ease;
  overflow: hidden;
}

.children-container.collapsing {
  max-height: 0;
  opacity: 0;
}

.children-container.expanding {
  max-height: 1000px;
  opacity: 1;
}
</style>
